import { Action, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { setAuthHeader } from '@fora-monorepo/webapp-auth';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';
import { addAdminMetaData } from '@fora-monorepo/webapp-analytics';

import { AuthState } from './types';
import { getUser } from './utils';
import { changeEmployerAndGetToken } from './authThunks';

export const initialAuthState: AuthState = {
  authenticated: false,
  user: null,
  loggedOut: false,
  loadingGetToken: ThunkRequestStatus.Pending,
  loadingChangeEmployer: null,
};

const isFulfilledAction = (action: Action) => {
  return (
    (action.type.startsWith('getToken') || action.type.startsWith('refreshToken')) && action.type.endsWith('fulfilled')
  );
};
const isRejectedAction = (action: Action) => {
  return (
    (action.type.startsWith('getToken') || action.type.startsWith('refreshToken')) && action.type.endsWith('rejected')
  );
};
const isPendingAction = (action: Action) => {
  return (
    (action.type.startsWith('getToken') || action.type.startsWith('refreshToken')) && action.type.endsWith('pending')
  );
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    logOut(state) {
      state.authenticated = false;
      state.user = null;
      state.loggedOut = true;
    },
  },
  extraReducers: builder => {
    builder.addCase(changeEmployerAndGetToken.fulfilled, state => {
      state.loadingChangeEmployer = ThunkRequestStatus.Fulfilled;
    });
    builder.addCase(changeEmployerAndGetToken.pending, state => {
      state.loadingChangeEmployer = ThunkRequestStatus.Pending;
    });
    builder.addCase(changeEmployerAndGetToken.rejected, state => {
      state.loadingChangeEmployer = ThunkRequestStatus.Rejected;
    });
    builder.addMatcher(isFulfilledAction, (state, action: PayloadAction<{ accessToken: string }>) => {
      if (action.payload?.accessToken) {
        state.loadingGetToken = ThunkRequestStatus.Fulfilled;
        state.authenticated = true;

        setAuthHeader(action.payload.accessToken);

        const user = getUser(action.payload.accessToken);
        state.user = user;

        if (user) {
          sessionStorage.setItem('selectedPartnerNumberOrganization', user.selectedPartnerNumberOrganization ?? '');
        }

        if (user?.partnerNumberIndividual) addAdminMetaData(user?.partnerNumberIndividual);
      }
    });
    builder.addMatcher(isPendingAction, state => {
      state.loadingGetToken = ThunkRequestStatus.Pending;
    });
    builder.addMatcher(isRejectedAction, state => {
      state.loadingGetToken = ThunkRequestStatus.Rejected;
      state.authenticated = false;
    });
  },
});

export const { logOut } = authSlice.actions;

export const authReducer = authSlice.reducer;
export default authSlice.reducer; // for unit tests
